import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Col, InputNumber, Row, Select } from 'antd'
import '../../css/schedule.scss'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FormOutlined,
} from '@ant-design/icons'
import { setExercises, setSelected } from '../../store/action/schedule.action'
import serviceInterface from '../../services/service.interface'
import { EXERCISE_ACTION_TYPE } from '../../services/schedule.service'
import moment from 'moment'
import TracPatchOverrideForm from './tracpatch.override.form'
import TracPatchConfigModal from './tracpatch.config.modal'
import { resolveWeekCell, currentWeekFromSurgery } from './tracpatch.week.defaults'

// For TracPatch exercises, auto-derive the override fields that mirror the
// standard schedule inputs (HOLD seconds + REPEAT count) so the clinician
// doesn't have to type them twice. The override form is still available
// for the angle thresholds + posture/mode tuning — those manual edits win
// over the auto-derived values via shallow-merge ({...auto, ...manual}).
//
//   REPEAT=N    →  requiredReps=N (always — REPEAT and requiredReps are 1:1
//                                  regardless of mode)
//   HOLD=0      →  mode='reps'
//   HOLD>0 (s)  →  mode='hold', requiredHoldSeconds=HOLD
//
// SET and DAILY don't map to TracPatch config (SET is unused, DAILY is
// tracked on schedule.exercise.time and pinned to 1 for TracPatch).
function deriveTracpatchOverridesFromStandard({ hold, holdPostfix, repeat }) {
  var holdNum = parseInt(hold, 10) || 0
  var holdSec = holdPostfix === 'minute' ? holdNum * 60 : holdNum
  var repeatNum = parseInt(repeat, 10) || 0
  var auto = {}
  if (holdSec > 0) {
    auto.mode = 'hold'
    auto.requiredHoldSeconds = holdSec
  } else {
    auto.mode = 'reps'
  }
  // REPEAT is NOT mirrored into requiredReps any more: the schedule's repeat
  // is the care plan's value and governs the patient's rep target directly
  // (2026-10-16). `repeatNum` is kept in the signature for callers.
  void repeatNum
  return auto
}

const Configure = (props) => {
  var seperateHoldString = (string) => {
    if (!string) return { postfix: 'second', num: 0 }
    var num = string.match(/\d+/g)
    // num[0] will be a number
    var letr = string.match(/[a-zA-Z]+/g)
    /* letr[0] will be a string.*/
    return { postfix: letr ? letr[0] : null, num: num ? num[0] : 0 }
  }

  var { hold, repeat, time, set, tracpatchConfigOverrides, ...rest } = props.settings
  var holdMod = seperateHoldString(hold)

  // Detect if this is a TracPatch exercise — the exercise sub-object on the
  // schedule entry carries the type + tracpatchConfig (master defaults).
  var isTracPatch =
    props.settings.exercise && props.settings.exercise.type === 'TracPatch'
  var tracpatchMasterConfig =
    props.settings.exercise ? props.settings.exercise.tracpatchConfig : null

  // TracPatch exercises seed hold/repeat/set defaults from the master
  // tracpatchConfig (requiredHoldSeconds / requiredReps / requiredSets)
  // so the admin sees the clinically-set defaults instead of an empty
  // row. DAILY is always pinned to 1 — TracPatch is single-slot
  // system-wide. Standard exercises keep their original behavior
  // (values come straight from props.settings).
  var mc = tracpatchMasterConfig || {}
  var initHoldNum = isTracPatch
    ? (parseInt(holdMod.num, 10) > 0
        ? holdMod.num
        : (parseInt(mc.requiredHoldSeconds, 10) > 0 ? mc.requiredHoldSeconds : 0))
    : holdMod.num
  var initRepeat = isTracPatch
    ? (parseInt(repeat, 10) > 0
        ? repeat
        : (parseInt(mc.requiredReps, 10) > 0 ? mc.requiredReps : 0))
    : repeat
  var initSet = isTracPatch
    ? (parseInt(set, 10) > 0
        ? set
        : (parseInt(mc.requiredSets, 10) > 0 ? mc.requiredSets : 1))
    : set
  var initTime = isTracPatch ? 1 : time

  // For TracPatch, the card's REPEAT reflects the reps required for the
  // patient's CURRENT post-op week — resolved through the same chain the
  // override form / patient session use (per-patient per-week → per-patient
  // requiredReps → clinic per-week → tier/master). Non-TracPatch rows keep
  // their stored repeat. Recomputes as props.settings (overrides) change.
  var tracpatchCurrentWeekReps = (function () {
    if (!isTracPatch) return null
    var surgeryDate =
      props.newPatient && props.newPatient.surgery && props.newPatient.surgery.date
    var curWeek = currentWeekFromSurgery(surgeryDate, 13)
    var ovr = tracpatchConfigOverrides || {}
    return resolveWeekCell(curWeek, 'reps', {
      repMilestoneByWeek: ovr.rep_milestone_by_week,
      resolved: function (f) {
        return ovr[f] !== undefined && ovr[f] !== null ? ovr[f] : mc[f]
      },
      tierDefaults: mc.tierDefaults,
      clinicRepMilestoneByWeek: mc.rep_milestone_by_week,
      overrides: ovr,
    })
  })()
  var displayRepeat =
    isTracPatch && tracpatchCurrentWeekReps != null
      ? tracpatchCurrentWeekReps
      : repeat

  const [edit, setEdit] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [input, setInput] = useState({ hold: initHoldNum, repeat: initRepeat, time: initTime, set: initSet })
  const [tracpatchOverrides, setTracpatchOverrides] = useState(tracpatchConfigOverrides || {})
  const [postfix, setPostfix] = useState({ hold: holdMod.postfix })

  const handleModalSave = (modalInput, modalOverrides) => {
    // Resolve the final HOLD/REPEAT values. For TracPatch exercises the
    // override form's requiredHoldSeconds / requiredReps win — they
    // propagate back into the standard schedule fields so the
    // 4-field display row (HOLD, REPEAT, SET, DAILY) reflects them
    // immediately after Save.
    var finalHoldNum = parseInt(modalInput.hold, 10) || 0
    var finalHoldPostfix = modalInput.holdPostfix || 'second'
    var finalRepeat = modalInput.repeat

    if (isTracPatch && modalOverrides) {
      if (
        modalOverrides.requiredHoldSeconds != null &&
        modalOverrides.requiredHoldSeconds > 0
      ) {
        finalHoldNum = modalOverrides.requiredHoldSeconds
        finalHoldPostfix = 'second'
      }
      // REPEAT is the care plan's reps and is edited as itself in the modal;
      // it is never taken from a requiredReps override (2026-10-16).
    }

    var tpOverrides = null
    if (isTracPatch) {
      var auto = deriveTracpatchOverridesFromStandard({
        hold: finalHoldNum,
        holdPostfix: finalHoldPostfix,
        repeat: finalRepeat,
      })
      // After propagating overrides → standard fields above, the override
      // form's explicit values are now in sync. Manual overrides still
      // shallow-merge on top so non-standard fields (angles, posture,
      // mode, etc.) are preserved.
      var merged = Object.assign({}, auto, modalOverrides || {})
      // Reps live on the schedule row (`repeat`, the care plan's value) and
      // nowhere in the TracPatch overrides (2026-10-16): no flat requiredReps,
      // and no `reps` on the per-week rows. Stale ones are dropped on save so
      // the apps' fallback chain can never pick them up.
      delete merged.requiredReps
      if (Array.isArray(merged.rep_milestone_by_week)) {
        var weekRows = merged.rep_milestone_by_week
          .map(function (row) {
            if (!row) return null
            var copy = Object.assign({}, row)
            delete copy.reps
            var hasValue = Object.keys(copy).some(function (k) {
              return k !== 'week' && copy[k] !== undefined && copy[k] !== null
            })
            return hasValue ? copy : null
          })
          .filter(Boolean)
        if (weekRows.length) merged.rep_milestone_by_week = weekRows
        else delete merged.rep_milestone_by_week
      }
      // Hold still mirrors the schedule HOLD field (it is not per-week).
      var finalHoldSec = finalHoldPostfix === 'minute' ? finalHoldNum * 60 : finalHoldNum
      if (finalHoldSec > 0) merged.requiredHoldSeconds = finalHoldSec
      else delete merged.requiredHoldSeconds
      tpOverrides = Object.keys(merged).length > 0 ? merged : null
    } else if (modalOverrides && Object.keys(modalOverrides).length > 0) {
      tpOverrides = modalOverrides
    }

    var payload = {
      ...rest,
      hold: finalHoldNum != 0 ? finalHoldNum + ' ' + finalHoldPostfix : null,
      repeat: String(finalRepeat),
      time: modalInput.time.toString(),
      set: modalInput.set.toString(),
      tracpatchConfigOverrides: tpOverrides,
    }
    console.log('[tracpatch][handleModalSave]', {
      modalOverrides_rep_milestone_by_week: modalOverrides && modalOverrides.rep_milestone_by_week,
      tpOverrides_rep_milestone_by_week: tpOverrides && tpOverrides.rep_milestone_by_week,
      payload_rep_milestone_by_week: payload.tracpatchConfigOverrides && payload.tracpatchConfigOverrides.rep_milestone_by_week,
    })
    props.setExercises(
      serviceInterface.ScheduleService().modifyExerciseArray(
        payload,
        props.exercises,
        EXERCISE_ACTION_TYPE.EDIT
      )
    )
    // Sync local edit-state copies so the read-mode row reflects the new values.
    setInput({ hold: finalHoldNum, repeat: finalRepeat, time: modalInput.time, set: modalInput.set })
    setPostfix({ hold: finalHoldPostfix })
    setTracpatchOverrides(modalOverrides || {})
    setModalOpen(false)
  }

  useEffect(() => {
    props.editChange(edit)
    props.setOthersEdit(edit)
  }, [edit])

  var handleChange = (type, value) => setInput({ ...input, [type]: value })

  var handleSubmit = () => {
    var payload = {
      ...rest,
      hold: input.hold != 0 ? input.hold + ' ' + postfix.hold : null,
      repeat: input.repeat.toString(),
      time: input.time.toString(),
      set: input.set.toString(),
    }
    // For TracPatch exercises, derive override values from the standard
    // HOLD/REPEAT inputs so the clinician doesn't have to enter them
    // twice. Any explicit edits in the override form (mode, angles, etc.)
    // shallow-merge on top and win over the auto-derived ones.
    if (isTracPatch) {
      var autoTp = deriveTracpatchOverridesFromStandard({
        hold: input.hold,
        holdPostfix: postfix.hold,
        repeat: input.repeat,
      })
      var mergedTp = Object.assign({}, autoTp, tracpatchOverrides || {})
      // Reps stay on the schedule row (`repeat`); never a requiredReps
      // override (same rule as handleModalSave, 2026-10-16).
      delete mergedTp.requiredReps
      var inputHoldNum = parseInt(input.hold, 10) || 0
      var inputHoldSec = postfix.hold === 'minute' ? inputHoldNum * 60 : inputHoldNum
      if (inputHoldSec > 0) mergedTp.requiredHoldSeconds = inputHoldSec
      else delete mergedTp.requiredHoldSeconds
      payload.tracpatchConfigOverrides =
        Object.keys(mergedTp).length > 0 ? mergedTp : null
    }
    props.setExercises(
      serviceInterface.ScheduleService().modifyExerciseArray(
        payload,
        props.exercises,
        EXERCISE_ACTION_TYPE.EDIT
      )
    )
    setEdit(false)
  }

  var handleCancel = () => {
    setInput({ hold: holdMod.num, repeat, time })
    setPostfix({ hold: holdMod.postfix })
    setTracpatchOverrides(tracpatchConfigOverrides || {})
    setEdit(false)
  }

  var disabled = () => {
    return (
      moment()
        .startOf('day')
        .isAfter(
          moment(props.newPatient?.surgery?.date)
            .add(rest.startdate, 'd')
            .startOf('day')
        ) &&
      moment()
        .startOf('day')
        .isAfter(
          moment(props.newPatient?.surgery?.date)
            .add(rest.enddate, 'd')
            .startOf('day')
        )
    )
  }

  var isSelectedOne = props.isSelected && props.selectedExer._id == rest._id

  var handleSelect = () => {
    if (isSelectedOne) return props.setSelected({})
    if (!props.isSelected) return props.setSelected(props.settings)
    var exercises = serviceInterface
      .ScheduleService()
      .connectExercises(props.settings, props.selectedExer, props.exercises)
    props.setExercises(exercises)
    props.setSelected({})
  }

  var allowToConnect =
    props.isSelected &&
    props.connect &&
    serviceInterface
      .ScheduleService()
      .allowToSelect(props.settings, props.exercises, props.selectedExer)

  var svgClassName = () => {
    var className = 'connectorCircle '
    if (props.isSelected) className += 'hoverGreen '
    if (props.connect && allowToConnect) className += 'greenCircle '
    if (
      (props.connect && !allowToConnect) ||
      (props.connected && props.isSelected)
    )
      className += 'blueCircle hoverNone noAction '
    if (props.connected && !props.isSelected)
      className += 'blueCircle hoverGrey '
    if (isSelectedOne) className += 'redCircle '
    return className
  }

  return (
    <Row hidden={props.hidden} align='middle' style={{ height: '100%' }}>
      <Col flex={'10%'}>
        <Row justify='center'>
          <svg
            className={svgClassName()}
            viewBox='-1 -1 2 2'
            height={24}
            width={24}
          >
            <circle onClick={handleSelect} cx='0' cy='0' r={1} />
          </svg>
        </Row>
      </Col>
      <Col flex={'auto'}>
        <Row>
          <Col className='confHeader' flex={'80px'}>
            {' '}
            HOLD{' '}
          </Col>
          {!edit ? (
            <Col className='confHeader' flex={'auto'}>
              {hold}
            </Col>
          ) : (
            <Col className='confHeader' flex={'auto'}>
              <Row>
                <InputNumber
                  onChange={(e) => handleChange('hold', e)}
                  min={0}
                  className='confInput'
                  value={input.hold}
                />
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  className='confInput'
                  onChange={(hold) => setPostfix({ hold })}
                  value={postfix.hold}
                >
                  <Select.Option value={input.hold > 1 ? 'minutes' : 'minute'}>
                    {input.hold > 1 ? 'minutes' : 'minute'}
                  </Select.Option>
                  <Select.Option value={input.hold > 1 ? 'seconds' : 'second'}>
                    {input.hold > 1 ? 'seconds' : 'second'}
                  </Select.Option>
                </Select>
              </Row>
            </Col>
          )}
        </Row>
        <Row>
          <Col className='confHeader' flex={'80px'}>
            {' '}
            REPEAT{' '}
          </Col>
          {!edit ? (
            <Col className='confHeader' flex={'auto'}>
              {displayRepeat}&nbsp;{displayRepeat > 1 ? 'times' : 'time'}
            </Col>
          ) : (
            <Col className='confHeader' flex={'auto'}>
              <Row>
                <InputNumber
                  min={0}
                  onChange={(e) => handleChange('repeat', e)}
                  value={input.repeat}
                />
                &nbsp;{input.repeat > 1 ? 'times' : 'time'}
              </Row>
            </Col>
          )}
        </Row>
        <Row>
          <Col className='confHeader' flex={'80px'}>
            {' '}
            SET{' '}
          </Col>
          {!edit ? (
            <Col className='confHeader' flex={'auto'}>
              {set}&nbsp;{set > 1 ? 'times' : 'time'}
            </Col>
          ) : (
            <Col className='confHeader' flex={'auto'}>
              <Row>
                <InputNumber
                  min={0}
                  onChange={(e) => handleChange('set', e)}
                  value={input.set}
                />
                &nbsp;{input.set > 1 ? 'times' : 'time'}
              </Row>
            </Col>
          )}
        </Row>
        <Row>
                    <Col className="confHeader" flex={"80px"}> DAILY </Col>
                    {!edit ?
                        <Col className="confHeader" flex={"auto"}>{time}&nbsp;{time > 1 ? "times" : "time"}</Col> :
                        <Col className="confHeader" flex={"auto"}>
                            <Row>
                                <InputNumber min={0} onChange={(e) => handleChange('time', e)} value={input.time}/>
                                &nbsp;{input.time > 1 ? "times" : "time"}
                            </Row>
                        </Col>
                    }
                </Row>
        {/* TracPatch per-patient overrides are now edited in a modal opened
            from the edit (pencil) icon. The modal also includes the schedule
            inputs and a live preview of the patient-facing card. */}
        {isTracPatch && modalOpen && (
          <TracPatchConfigModal
            visible={modalOpen}
            settings={props.settings}
            masterConfig={tracpatchMasterConfig}
            initialInput={{
              hold: holdMod.num,
              holdPostfix: holdMod.postfix || 'second',
              repeat: input.repeat,
              time: input.time,
              set: input.set,
            }}
            initialOverrides={tracpatchOverrides}
            onCancel={() => setModalOpen(false)}
            onSave={handleModalSave}
            surgeryDate={props.newPatient && props.newPatient.surgery && props.newPatient.surgery.date}
          />
        )}
      </Col>
      <Col flex={edit ? '65px' : '30px'}>
        <Row align='middle' gutter={5}>
          <Col
            hidden={edit || (props.editPage && disabled()) || props.editOhers}
          >
            <FormOutlined
              className='editIcon'
              onClick={() => (isTracPatch ? setModalOpen(true) : setEdit(!edit))}
            />
          </Col>
          <Col hidden={!edit}>
            <CloseCircleOutlined className='editIcon' onClick={handleCancel} />
          </Col>
          <Col hidden={!edit}>
            <CheckCircleOutlined className='editIcon' onClick={handleSubmit} />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

Configure.propTypes = {
  customer: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    customer: state.profile.customer,
    newPatient: state.patient.newpatient,
    exercises: state.schedule.exercises,
    selectedExer: state.schedule.selected,
    isSelected: Object.keys(state.schedule.selected).length > 0,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setExercises: (exercises) => dispatch(setExercises(exercises)),
    setSelected: (exercise) => dispatch(setSelected(exercise)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Configure)
